import { useState } from 'react';
import { Link } from 'react-router-dom';
import ContactNavbar from './components/ContactNavbar';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formBody = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => {
      formBody.append(key, value);
    });

    try {
      await fetch('https://readdy.ai/api/form/d64a5j1ov5qgbon329jg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        budget: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <ContactNavbar />

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5daa7efc7b481dc7a9fd8bc8d920e44f.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/70 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Let's Talk
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Ready to transform your social media presence? Get in touch and let's discuss how we can help your brand grow.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Get in Touch
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Whether you're looking to boost your social media presence, create engaging content, or run targeted ad campaigns, we're here to help. Fill out the form and we'll get back to you within 24 hours.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00989e]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-[#00989e]"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
                    <a href="mailto:maria@unuu.co.uk" className="text-[#00989e] hover:underline cursor-pointer">
                      maria@unuu.co.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00989e]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-[#00989e]"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Call Us</h3>
                    <a href="tel:+447810306008" className="text-[#00989e] hover:underline cursor-pointer">
                      +44 7810 306 008
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00989e]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-[#00989e]"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Location</h3>
                    <p className="text-gray-600">United Kingdom</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12">
                <h3 className="font-semibold text-slate-900 mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center hover:bg-[#00989e] transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center hover:bg-[#00989e] transition-colors cursor-pointer">
                    <i className="ri-tiktok-line text-white text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center hover:bg-[#00989e] transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-white text-xl"></i>
                  </a>
                  <a href="#" className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center hover:bg-[#00989e] transition-colors cursor-pointer">
                    <i className="ri-twitter-x-line text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-[#00989e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-[#00989e]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Thank You!</h3>
                  <p className="text-gray-600 mb-8">
                    Your message has been sent successfully. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="px-6 py-3 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  id="contact-inquiry-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm"
                        placeholder="+44 7XXX XXX XXX"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-2">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm cursor-pointer bg-white"
                      >
                        <option value="">Select a service</option>
                        <option value="Social Media Management">Social Media Management</option>
                        <option value="Video & Reels Production">Video & Reels Production</option>
                        <option value="Influencer Marketing">Influencer Marketing</option>
                        <option value="Paid Social Advertising">Paid Social Advertising</option>
                        <option value="Content Strategy">Content Strategy</option>
                        <option value="Analytics & Reporting">Analytics & Reporting</option>
                        <option value="Full Service Package">Full Service Package</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="budget" className="block text-sm font-medium text-slate-700 mb-2">
                        Monthly Budget
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm cursor-pointer bg-white"
                      >
                        <option value="">Select budget range</option>
                        <option value="Under £1,000">Under £1,000</option>
                        <option value="£1,000 - £3,000">£1,000 - £3,000</option>
                        <option value="£3,000 - £5,000">£3,000 - £5,000</option>
                        <option value="£5,000 - £10,000">£5,000 - £10,000</option>
                        <option value="£10,000+">£10,000+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00989e] focus:border-transparent outline-none transition-all text-sm resize-none"
                      placeholder="Tell us about your project and goals..."
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {formData.message.length}/500 characters
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600">
              Quick answers to common questions about working with us.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'How quickly can you start working on my project?',
                answer: 'We typically begin onboarding within 1-2 weeks of signing. This includes strategy sessions, brand immersion, and content planning before we go live.'
              },
              {
                question: 'What platforms do you specialise in?',
                answer: 'We specialise in Instagram, TikTok, LinkedIn, Facebook, Pinterest, and YouTube. We\'ll recommend the best platforms based on your target audience and goals.'
              },
              {
                question: 'Do you offer custom packages?',
                answer: 'Absolutely! While we have standard packages, we understand every brand is unique. We\'ll create a tailored solution that fits your specific needs and budget.'
              },
              {
                question: 'How do you measure success?',
                answer: 'We track key metrics including engagement rates, follower growth, reach, website traffic, and conversions. You\'ll receive detailed monthly reports with actionable insights.'
              }
            ].map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-50 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="font-semibold text-slate-900 pr-4">{faq.question}</h3>
                  <i className="ri-add-line text-xl text-[#00989e] group-open:hidden"></i>
                  <i className="ri-subtract-line text-xl text-[#00989e] hidden group-open:block"></i>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
